import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { RunbookTable } from '@/features/objects/components/RunbookTable'
import { calculateRunbookTimes } from '@/features/objects/utils/calculateRunbookTimes'
import { masterRunbooksApi } from '../api'
import type { MasterRunbook } from '../api'
import type { RunbookTask } from '@/types/entities'

interface MasterRunbookViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    masterRunbook: MasterRunbook | null
}

export function MasterRunbookViewDialog({ open, onOpenChange, masterRunbook }: MasterRunbookViewDialogProps) {
    const { t } = useTranslation()
    const [tasks, setTasks] = useState<RunbookTask[]>([])
    const [information, setInformation] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (open && masterRunbook) {
            setIsLoading(true)
            masterRunbooksApi.get(masterRunbook.id)
                .then((response) => {
                    const data = response.data?.data || response.data
                    setInformation(data.information || '')
                    // Calculate TaskOffset and times for display
                    const tasksWithTimes = calculateRunbookTimes(data.tasks || [])
                    setTasks(tasksWithTimes)
                })
                .catch((error) => {
                    console.error('Error loading master runbook:', error)
                    setTasks([])
                    setInformation('')
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }
    }, [open, masterRunbook])

    if (!masterRunbook) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{masterRunbook.description}</DialogTitle>
                    <DialogDescription>
                        {t('masterRunbooks.viewMasterRunbookDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {information && (
                        <div>
                            <label className="text-sm font-medium text-muted-foreground mb-2 block">
                                {t('masterRunbooks.information')}
                            </label>
                            <div 
                                className="prose prose-sm max-w-none border rounded-md p-4 bg-muted/50"
                                dangerouslySetInnerHTML={{ __html: information }}
                            />
                        </div>
                    )}

                    <div>
                        <label className="text-sm font-medium text-muted-foreground mb-2 block">
                            {t('objects.runbook.tasks')}
                        </label>
                        {isLoading ? (
                            <div className="text-center py-8 text-muted-foreground">
                                {t('common.loading')}
                            </div>
                        ) : (
                            <div className="[&_button]:hidden [&_textarea]:pointer-events-none [&_input]:pointer-events-none [&_textarea]:cursor-default [&_input]:cursor-default">
                                <RunbookTable
                                    tasks={tasks}
                                    onSave={() => {}} // Read-only in view mode
                                    autoUpdate={false}
                                    onAutoUpdateChange={() => {}}
                                    hideToolbar={true}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
